import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

import { API } from "@/lib/apiBase";

// Always send cookies (HttpOnly auth cookie)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.warn("auth/me failed", err?.response?.status);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password, captcha = {}) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, ...captcha });
    setUser(res.data.user);
    return res.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback(async (payload) => {
    const res = await axios.post(`${API}/auth/register`, payload);
    setUser(res.data.user);
    return res.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.warn("logout request failed", err);
    }
    setUser(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth travels via HttpOnly cookie; axios.defaults.withCredentials ensures it is sent automatically.
  const value = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
